import { BlockEventType } from "@prisma/client";
import {
  ActionIdPlaceholderSchema,
  BlockBaseSchema,
  BlockIdSchema,
  ComponentBlockStyleSchema,
} from "blocks/types/base";
import { config } from "server/config";
import { z, ZodSchema } from "zod";

// Chatbot block
// -----------------------------------------------------------------------------

/**
 * When adding new models --
 * 1. Add the model value to this enum, and add the model to the `ChatModels` object below.
 * 2. Make sure our `tiktoken` version supports the model, we use this to calculate usage.
 *   - https://github.com/dqbd/tiktoken/blob/main/tiktoken/model_to_encoding.json
 */
export const chatModelValues = z.enum([
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-16k",
  "gpt-4",
  "gpt-4-32k",
]);

export const ChatModelSchema = z.object({
  label: z.string(),
  value: chatModelValues,
  contextLimit: z.number().int(),
  requiresUpgrade: z.boolean().default(true),
  supportedVersion: z.string(),
});
export type ChatModel = z.infer<typeof ChatModelSchema>;

export const ChatModels: Record<string, ChatModel> = {
  GPT3: {
    label: "OpenAI GPT-3.5",
    value: "gpt-3.5-turbo",
    contextLimit: 4096,
    requiresUpgrade: false,
    supportedVersion: "0613",
  },
  GPT3_16k: {
    label: "OpenAI GPT-3.5 (16k)",
    value: "gpt-3.5-turbo-16k",
    contextLimit: 16384,
    requiresUpgrade: true,
    supportedVersion: "0613",
  },
  GPT4: {
    label: "OpenAI GPT-4",
    value: "gpt-4",
    contextLimit: 8192,
    requiresUpgrade: true,
    supportedVersion: "0613",
  },
  GPT4_32k: {
    label: "OpenAI GPT-4 (32k)",
    value: "gpt-4-32k",
    contextLimit: 32768,
    requiresUpgrade: true,
    supportedVersion: "0613",
  },
};

export const chatModelsAllowedWithoutBYOK: ChatModel[] = Object.values(
  ChatModels
).filter((m) => !m.requiresUpgrade);

export const chatModelsRequiringBYOK: ChatModel[] = Object.values(
  ChatModels
).filter((m) => m.requiresUpgrade);

export const getChatModelByName = (chatModelName: string): ChatModel => {
  const model = Object.values(ChatModels).find(
    (m) => m.value === chatModelName
  );

  if (!model) {
    throw new Error(`ChatModel with name "${chatModelName}" not found`);
  }

  return model;
};

// Chat completion
// -----------------------------------------------------------------------------

export const MessageSchema = z.discriminatedUnion("role", [
  z.object({
    content: z.string(),
    role: z.enum(["system", "assistant", "user"]),
    name: z.string().optional(),
  }),
  z.object({
    content: z.string(),
    role: z.literal("function"),
    name: z.string(),
  }),
]);

export const FunctionSchema = z.object({
  name: z.string(),
  description: z.string(),
  parameters: z
    .object({
      type: z.literal("object"),
      properties: z.record(
        z.object({
          type: z.string(),
          description: z.string(),
          enum: z.string().array().optional(),
        })
      ),
      required: z.string().array(),
    })
    .optional(),
});

// Chatbot actions
// i.e. Actions that are stored in the chabot block config in the page content
// -----------------------------------------------------------------------------

export const ChatbotButtonActionSchema = z.object({
  type: z.literal("chatbot-button"),
  config: z.object({
    id: z.string().cuid().or(ActionIdPlaceholderSchema).optional(),
    userEvent: z.literal("response"),
    action: z.literal("button"),
    buttonText: z
      .string()
      .min(1, "This field is required")
      .max(30, "This field must be no more than 30 characters"),
    onClick: z.union([z.literal("copy"), z.literal("zap")]),
    notificationText: z
      .string()
      .min(1, "This field is required")
      .max(50, "This field must be no more than 50 characters")
      .optional()
      .default("Action triggered!"),
  }),
});
export type ChatbotButtonAction = z.infer<typeof ChatbotButtonActionSchema>;

export const ChatbotActionSchema = z.discriminatedUnion("type", [
  ChatbotButtonActionSchema,
]);
export type ChatbotAction = z.infer<typeof ChatbotActionSchema>;

export const ChatbotButtonEventValuesSchema = z.object({
  response: z.string(),
  chatbotName: z.string().optional(),
  prompt: z.string().optional(),
});

export const ChatbotFeedbackEventValuesSchema = z.object({
  message: z.string(),
  rating: z.number().min(-1).max(1),
  comment: z.string().optional(),
  conversation: z.array(MessageSchema),
});

export const ChatbotButtonEventSchema = z.object({
  type: z.literal(BlockEventType.ChatbotButtonClick),
  blockActionId: z.string().cuid(),
  values: ChatbotButtonEventValuesSchema,
});

export const ChatbotFeedbackEventSchema = z.object({
  type: z.literal(BlockEventType.ChatbotFeedback),
  blockId: z.string().cuid(),
  chatbotSessionId: z.string().cuid(),
  values: ChatbotFeedbackEventValuesSchema,
});

export type ChatbotFeedbackActionEvent = z.infer<
  typeof ChatbotFeedbackEventValuesSchema
>;

export const ChatbotCreateEventSchema = z.discriminatedUnion("type", [
  ChatbotButtonEventSchema,
  ChatbotFeedbackEventSchema,
]);

export type ChatbotCreateEvent = z.infer<typeof ChatbotCreateEventSchema>;

export function chatbotEventSchemaForAction(
  blockAction: ChatbotAction
): ZodSchema {
  if (blockAction.type === "chatbot-button") {
    return ChatbotButtonEventValuesSchema;
  }

  throw new Error("Invalid block action type");
}

// Chatbot data sources
// -----------------------------------------------------------------------------

export const MetadataSchema = z.object({
  accountId: z.string(),
  projectId: z.string(),
  pageId: z.string(),
  blockId: z.string(),
  name: z.string(),
  reference: z.string(),
  text: z.string(),
});

export type Metadata = z.infer<typeof MetadataSchema>;

const BaseSourceSchema = z.object({
  id: z.string().cuid(),
  description: z.string().optional().default(""),
  splitStrategy: z
    .enum(["auto", "delimiter", "ai", "manual"])
    .optional()
    .default("auto"),
  splitValue: z.string().optional().default(""),
  chunks: z
    .array(
      z.object({
        id: z.string(),
        metadata: MetadataSchema,
      })
    )
    .optional()
    .default([]),
});

const TableSourceSchema = BaseSourceSchema.extend({
  dataType: z.literal("table"),
  reference: z.string().min(26).max(26),
  name: z.string(),
});
export type TableSource = z.infer<typeof TableSourceSchema>;

export const FileSourceSchema = BaseSourceSchema.extend({
  dataType: z.literal("file"),
  reference: z.string().url(),
  name: z.string(),
});
export type FileSource = z.infer<typeof FileSourceSchema>;

const UrlSourceSchema = BaseSourceSchema.extend({
  dataType: z.literal("url"),
  reference: z
    .string()
    .min(4, { message: "URL must be at least 4 characters" }),
  name: z.string(),
});
export type UrlSource = z.infer<typeof UrlSourceSchema>;

export const SourceSchema = z.discriminatedUnion("dataType", [
  TableSourceSchema,
  FileSourceSchema,
  UrlSourceSchema,
]);

export type Source = z.infer<typeof SourceSchema>;

// Chatbot functions
// -----------------------------------------------------------------------------
export const ChatbotZapFunctionSchema = z.object({
  functionName: z.string(), // TODO: validate this further?
  description: z.string().optional(),
  // confirmFirst: z.boolean().default(true),
  zapId: z.number(),
  zapInput: z
    .record(
      z.string(),
      z.object({
        description: z.string().optional(),
        shouldGuess: z.boolean().default(true),
        staticValue: z.string().optional(), // TODO: support handlebars placeholder for these!
        required: z.boolean().default(true),
      })
    )
    .optional()
    .default({}),
});

export type ChatbotZapFunction = z.infer<typeof ChatbotZapFunctionSchema>;

// Chatbot schema
// -----------------------------------------------------------------------------

export const ChatbotStyleSchema = ComponentBlockStyleSchema.extend({
  height: z.union([z.literal("default"), z.literal("custom")]).optional(),
  heightValue: z.number().optional(),
});

export const ChatbotBlockSchema = BlockBaseSchema.extend({
  type: z.literal("chatbot-block"),
  config: z.object({
    id: BlockIdSchema.optional(),
    chatbotName: z.string().optional(),
    defaultMessageMode: z
      .enum(["static", "generated"])
      .optional()
      .default("generated"),
    defaultPrompt: z.string().optional(),
    defaultMessage: z.string().optional(),
    placeholder: z.string().optional(),
    directive: z.string().optional(),
    useDirectiveToAlterDefaultMessage: z.boolean().optional().default(true),
    authenticationId: z.string().optional().default(""),
    model: chatModelValues.optional().default("gpt-3.5-turbo"),
    maxTokens: z.coerce
      .number()
      .int()
      .optional()
      .default(config().OPENAI_MAX_TOKENS),
    temperature: z.number().min(0).max(1).optional().default(0.7), // can go to 2 but it's gibberish
    style: ChatbotStyleSchema.optional(),
    triggers: z.array(ChatbotActionSchema).default([]),
    displayDisclosure: z.boolean().default(true),
    disclosureText: z
      .string()
      .optional()
      .default(
        "This is an automated chatbot response. [Learn more](https://zapier.com/legal/terms-of-service)"
      ),
    dataSources: z.array(SourceSchema).optional().default([]),
    dataSourcesMaxResults: z.number().int().min(1).max(5).optional().default(5),
    dataSourcesResultsQuality: z
      .number()
      .min(0.6)
      .max(0.95)
      .optional()
      .default(0.6),
    dataSourcesMode: z
      .enum(["source", "chatgpt"])
      .optional()
      .default("chatgpt"),
    dataSourcesNoResultsMode: z
      .enum(["custom", "chatgpt"])
      .optional()
      .default("chatgpt"),
    dataSourcesNoResultsMessage: z
      .string()
      .optional()
      .default("Sorry, I don’t know."),
    zapFunctions: z.array(ChatbotZapFunctionSchema).default([]).optional(),
    embedHosts: z.string().array().optional().default([]),
    restrictEmbedDomains: z.boolean().optional().default(false),
  }),
}).openapi({ ref: "ChatbotBlock" });

export type ChatbotBlock = z.infer<typeof ChatbotBlockSchema>;

// Chatbot editor
// -----------------------------------------------------------------------------

export type ChatbotFocusStateData = {
  panel: "setup" | "data" | "automation" | "embed";
};
